/**
 * Trainer API – endpoints under /api/trainer.
 * Used by the trainer profile setup flow + any trainer-side dashboards.
 */

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? { 'x-auth-token': token } : {};
};

// Mirror the pattern in assessmentApi.js: REACT_APP_API_URL + /api, with a
// localhost fallback for dev.
const getApiBase = () => {
  const host = process.env.REACT_APP_API_URL;
  if (host) return `${host}/api`;
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    window.location.port === '3000'
  ) {
    return 'http://localhost:5000/api';
  }
  return '/api';
};

/**
 * Create or update the authenticated user's trainer profile.
 * Backend: POST /api/trainer/profile (requires auth + role:trainer).
 *
 * Side effects on success (server-side):
 *   - Creates Trainer document referencing the User
 *   - Sets User.trainerProfile to the new Trainer's _id
 *   - Sets User.isAssessmentCompleted = true (trainers skip client assessment)
 *
 * @param {Object} payload - { isIndependent, business: { name, bio?, colors, website?, phone?, logo? } }
 * @returns {Promise<Object>} Created trainer document
 */
export const createTrainerProfile = async (payload) => {
  const url = `${getApiBase()}/trainer/profile`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = new Error(res.statusText || 'Failed to create trainer profile');
    err.status = res.status;
    err.body = await res.json().catch(() => ({}));
    throw err;
  }
  return res.json();
};

/**
 * Public endpoint — accept a trainer invite via the setup token from
 * the email link. Validates the token, sets the trainer's chosen
 * password on Firebase, creates the Trainer doc, and returns a
 * Firebase custom token so the frontend can sign the trainer in
 * immediately. No `x-auth-token` is sent — the setupToken is the
 * authentication signal here.
 *
 * Backend: POST /api/trainer/accept-invite (public).
 *
 * @param {Object} payload  - { token, email, password, business, isIndependent? }
 *   The server requires `(token, email)` to both match the persisted
 *   invite — `email` is normally pre-filled from the `?email=…` query
 *   param the welcome email's link carries.
 * @returns {Promise<{customToken: string|null, user: object, trainer: object}>}
 */
export const acceptTrainerInvite = async (payload) => {
  const url = `${getApiBase()}/trainer/accept-invite`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = new Error(res.statusText || 'Could not accept invite');
    err.status = res.status;
    err.body = await res.json().catch(() => ({}));
    throw err;
  }
  return res.json();
};
